import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Tabs,
  List,
  Avatar,
  Alert,
  DatePicker,
  Select,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  Badge,
  InputNumber,
  Popconfirm,
  Upload,
  Divider,
  notification,
  Spin,
  Descriptions,
} from 'antd';

const { Option } = Select;
import { PersistentStorage } from '../../../utils/persistentStorage';
import { useIsMobile, getMobileTableProps, getMobileButtonProps, getSpaceDirection } from '../../../utils/responsiveUtils';
import {
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
  BarcodeOutlined,
  TruckOutlined,
  ShopOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
  ScanOutlined,
  AlertOutlined,
  HomeOutlined,
  BoxPlotOutlined,
  BarChartOutlined,
  CalendarOutlined,
  SearchOutlined,
  InboxOutlined,
  ReloadOutlined,
  SettingOutlined,
  PrinterOutlined,
  StockOutlined,
  SafetyOutlined,
  UserOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { InventoryDashboard } from '../components/InventoryDashboard';
import { BarcodeScannerComponent } from '../components/BarcodeScanner';
import { WarehouseManagement, WarehouseLocationManagement } from '../components/WarehouseManagement';
import { MassImportDialog } from '../../../components/import/MassImportDialog';
import useDraggableColumns from '../../../components/table/useDraggableColumns';
import type { 
  Warehouse, 
  BarcodeScanner, 
  BarcodeScans, 
  ScanSessionType,
  WarehouseZone,
  Location 
} from '../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs;

interface Product {
  id: string;
  sku: string;
  name: string; // Keep for backward compatibility
  nameEnglish?: string; // English product name
  nameChinese?: string; // Chinese product name
  category: string;
  description?: string;
  currentStock: number;
  reorderLevel: number;
  maxStock: number;
  unitPrice: number;
  costPrice: number;
  supplier: string;
  location: string;
  lastUpdated: string;
  status: 'in_stock' | 'low_stock' | 'out_of_stock' | 'discontinued';
  imageUrl?: string;
  barcode?: string;
  warehouse?: string;
  capacity?: number;
  // UOM Conversion Factor fields
  baseUOM?: string; // Base unit of measure (pcs, kg, etc.)
  packagingUOM?: string; // Packaging unit (box, carton, etc.)
  conversionFactor?: number; // 1 box = X pcs
  // Reference fields
  merchantReference?: string; // External merchant reference code
  internalReference?: string; // Internal reference code for tracking
}

interface StockMovement {
  id: string;
  productId: string;
  productName: string;
  type: 'in' | 'out' | 'adjustment' | 'transfer';
  quantity: number;
  reason: string;
  timestamp: string;
  user: string;
  referenceNumber?: string;
  fromLocation?: string;
  toLocation?: string;
  warehouse: string;
}

interface InventoryData {
  totalProducts: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
  products: Product[];
  stockMovements: StockMovement[];
  warehouses: Warehouse[];
}

interface StockAdjustment {
  productId: string;
  quantity: number;
  reason: string;
  reasonCode: string;
  notes?: string;
}

const InventoryOverview: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  
  const [inventoryData, setInventoryData] = useState<InventoryData>({
    totalProducts: 0,
    totalValue: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    products: [],
    stockMovements: [],
    warehouses: []
  });
  const [loading, setLoading] = useState(false);
  
  // Set active tab based on current route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/inventory/products')) return 'products';
    if (path.includes('/inventory/stock')) return 'stock';
    if (path.includes('/inventory/warehouses')) return 'warehouses';
    if (path.includes('/inventory/locations')) return 'locations';
    return 'overview';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [stockAdjustmentVisible, setStockAdjustmentVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [form] = Form.useForm();
  const [adjustmentForm] = Form.useForm();
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [scannerVisible, setScannerVisible] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [scanSessionType, setScanSessionType] = useState<ScanSessionType>('receiving');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [scanners, setScanners] = useState<BarcodeScanner[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [lowStockAlerts, setLowStockAlerts] = useState<Product[]>([]);
  const [reorderSuggestions, setReorderSuggestions] = useState<Product[]>([]);
  const [permissionError, setPermissionError] = useState<string>('');

  // Mock data for comprehensive testing
  const mockProducts: Product[] = [
    {
      id: '1',
      sku: 'LAP-DELL-XPS13',
      name: 'Dell XPS 13 Laptop',
      category: 'Electronics',
      description: 'High-performance ultrabook',
      currentStock: 45,
      reorderLevel: 20,
      maxStock: 100,
      unitPrice: 1299.99,
      costPrice: 999.99,
      supplier: 'Dell Inc.',
      location: 'A-01-01-01',
      lastUpdated: new Date().toISOString(),
      status: 'in_stock',
      barcode: '1234567890123',
      warehouse: 'main-warehouse',
      capacity: 100,
    },
    {
      id: '2',
      sku: 'PHN-IPH-14PRO',
      name: 'iPhone 14 Pro',
      category: 'Mobile',
      description: 'Latest iPhone model',
      currentStock: 5,
      reorderLevel: 20,
      maxStock: 80,
      unitPrice: 1099.99,
      costPrice: 899.99,
      supplier: 'Apple Inc.',
      location: 'B-02-01-01',
      lastUpdated: new Date().toISOString(),
      status: 'low_stock',
      barcode: '2345678901234',
      warehouse: 'main-warehouse',
      capacity: 80,
    },
    {
      id: '3',
      sku: 'TAB-SAM-S8',
      name: 'Samsung Galaxy Tab S8',
      category: 'Tablets',
      description: 'Premium Android tablet',
      currentStock: 0,
      reorderLevel: 15,
      maxStock: 60,
      unitPrice: 699.99,
      costPrice: 549.99,
      supplier: 'Samsung Electronics',
      location: 'C-01-01-01',
      lastUpdated: new Date().toISOString(),
      status: 'out_of_stock',
      barcode: '3456789012345',
      warehouse: 'main-warehouse',
      capacity: 60,
    },
  ];

  const mockWarehouses: Warehouse[] = [
    {
      id: 'main-warehouse',
      name: 'Main Warehouse',
      code: 'WH-MAIN',
      address: {
        street: '123 Industrial Ave',
        city: 'City',
        state: 'State',
        zipCode: '12345',
        country: 'Country',
      },
      capacity: 10000,
      currentUtilization: 7850,
      zones: [],
      isActive: true,
      settings: {
        barcodeFormat: 'CODE128',
        locationNamingConvention: '{Zone}-{Aisle}-{Rack}-{Shelf}',
        autoGenerateLocations: true,
        cycleCounting: {
          enabled: true,
          frequency: 'monthly',
        },
        putawayStrategy: 'fifo',
        pickingStrategy: 'fifo',
        waveManagement: {
          enabled: true,
          autoRelease: true,
        },
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  ];

  const mockScanners: BarcodeScanner[] = [
    {
      id: 'scanner-001',
      deviceId: 'ZBR-001',
      name: 'Zebra Scanner #1',
      type: 'handheld',
      connectionType: 'bluetooth',
      isActive: true,
      lastSeen: new Date(),
      batteryLevel: 85,
      firmwareVersion: '2.1.0',
      assignedUser: 'John Doe',
      warehouse: 'main-warehouse',
    },
  ];

  const reasonCodes = [
    'RECEIVED',
    'DAMAGED',
    'LOST',
    'FOUND',
    'CYCLE_COUNT',
    'EXPIRED',
    'RETURNED',
    'TRANSFER',
    'PRODUCTION',
    'MANUAL',
  ];

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  useEffect(() => {
    loadInventoryData();
    loadLowStockAlerts();
    generateReorderSuggestions();
  }, []);

  useEffect(() => {
    const filtered = inventoryData.products.filter(product => 
      product.name.toLowerCase().includes(searchText.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchText.toLowerCase()) ||
      product.category.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [inventoryData.products, searchText]);

  const loadInventoryData = async () => {
    try {
      setLoading(true);
      
      // Load persistent data, fallback to mock data if empty
      const persistentProducts = PersistentStorage.initializeWithMockData('products', mockProducts);
      const persistentWarehouses = PersistentStorage.initializeWithMockData('warehouses', mockWarehouses);
      const persistentStockMovements = PersistentStorage.get('stockMovements', [
        {
          id: '1',
          productId: '1',
          productName: 'Dell XPS 13 Laptop',
          type: 'in',
          quantity: 50,
          reason: 'Purchase receipt',
          timestamp: new Date().toISOString(),
          user: 'John Doe',
          warehouse: 'main-warehouse',
        },
        {
          id: '2',
          productId: '2',
          productName: 'iPhone 14 Pro',
          type: 'out',
          quantity: -25,
          reason: 'Sales order',
          timestamp: new Date().toISOString(),
          user: 'Jane Smith',
          warehouse: 'main-warehouse',
        },
      ]);

      // Calculate data from persistent storage
      const inventoryData: InventoryData = {
        totalProducts: persistentProducts.length,
        totalValue: persistentProducts.reduce((sum: number, p: any) => sum + (p.currentStock * p.unitPrice), 0),
        lowStockItems: persistentProducts.filter((p: any) => p.status === 'low_stock').length,
        outOfStockItems: persistentProducts.filter((p: any) => p.status === 'out_of_stock').length,
        products: persistentProducts,
        stockMovements: persistentStockMovements,
        warehouses: persistentWarehouses,
      };

      setInventoryData(inventoryData);
      setWarehouses(persistentWarehouses);
      setScanners(mockScanners);
    } catch (error) {
      message.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const loadLowStockAlerts = () => {
    const lowStockItems = mockProducts.filter(p => p.currentStock <= p.reorderLevel);
    setLowStockAlerts(lowStockItems);
    
    if (lowStockItems.length > 0) {
      notification.warning({
        message: 'Low Stock Alert',
        description: `${lowStockItems.length} items are below reorder level`,
        placement: 'topRight',
        duration: 4.5,
        icon: <AlertOutlined style={{ color: '#faad14' }} />,
      });
    }
  };

  const generateReorderSuggestions = () => {
    const suggestions = mockProducts.filter(p => p.currentStock <= p.reorderLevel).map(product => ({
      ...product,
      suggestedQuantity: product.maxStock - product.currentStock,
    }));
    setReorderSuggestions(suggestions);
  };

  const handleAddProduct = async (values: any) => {
    try {
      // Validation for required fields
      if (!values.nameEnglish || !values.nameChinese || !values.sku) {
        message.error('Required fields missing (English name, Chinese name, and SKU are required).');
        return;
      }

      // Check for duplicate SKU
      if (inventoryData.products.some(p => p.sku === values.sku)) {
        message.error('Duplicate SKU and prevents product creation.');
        return;
      }

      // Validate capacity
      if (!values.capacity || values.capacity <= 0) {
        message.error('Capacity required and prevents warehouse creation.');
        return;
      }

      // Validate UOM conversion factor
      if (!values.baseUOM || !values.packagingUOM || !values.conversionFactor) {
        message.error('UOM conversion fields are required (Base UOM, Packaging UOM, and Conversion Factor).');
        return;
      }

      if (values.conversionFactor < 1) {
        message.error('Conversion factor must be at least 1.');
        return;
      }

      // Validate location selection
      if (!values.location) {
        message.error('Location is required. Please select a warehouse location.');
        return;
      }

      // Validate supplier selection
      if (!values.supplier) {
        message.error('Supplier is required. Please select a supplier.');
        return;
      }

      const newProduct: Product = {
        id: Date.now().toString(),
        ...values,
        name: values.nameEnglish, // Use English name as default display name
        nameEnglish: values.nameEnglish,
        nameChinese: values.nameChinese,
        currentStock: values.initialStock || 0,
        lastUpdated: new Date().toISOString(),
        status: values.initialStock > values.reorderLevel ? 'in_stock' : 'low_stock',
        // UOM conversion fields
        baseUOM: values.baseUOM,
        packagingUOM: values.packagingUOM,
        conversionFactor: values.conversionFactor,
        // Reference fields
        merchantReference: values.merchantReference,
      };

      // Save to persistent storage
      const updatedProducts = PersistentStorage.addToArray('products', newProduct, []);

      setInventoryData(prev => ({
        ...prev,
        products: updatedProducts,
        totalProducts: updatedProducts.length,
      }));

      // Find location and supplier labels for success message
      const locationOptions = getLocationOptions();
      const selectedLocationLabel = locationOptions.find(opt => opt.value === values.location)?.label || values.location;
      
      const supplierOptions = getSupplierOptions();
      const selectedSupplierLabel = supplierOptions.find(opt => opt.value === values.supplier)?.label || values.supplier;

      const merchantRef = values.merchantReference ? ` - Merchant Ref: ${values.merchantReference}` : '';

      message.success(
        `Product added successfully: ${values.nameEnglish} (${values.nameChinese}) - Supplier: ${selectedSupplierLabel} - Location: ${selectedLocationLabel}${merchantRef} - UOM: 1 ${values.packagingUOM} = ${values.conversionFactor} ${values.baseUOM}`
      );
      setProductModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to add product');
    }
  };

  const handleImportComplete = (result: any) => {
    console.log('Import completed:', result);
    message.success(`導入完成！成功: ${result.successful}, 失敗: ${result.failed}`);
    setShowImportDialog(false);
    // 重新加載產品數據
    loadInventoryData();
  };

  const handleStockAdjustment = async (values: StockAdjustment) => {
    try {
      if (!selectedProduct) return;

      // Validate quantity
      if (values.quantity < 0 && Math.abs(values.quantity) > selectedProduct.currentStock) {
        message.error('Invalid quantity and prevents stock adjustment.');
        return;
      }

      // Validate reason code
      if (!values.reasonCode) {
        message.error('Reason code required and prevents stock adjustment.');
        return;
      }

      const newStock = selectedProduct.currentStock + values.quantity;
      const newStatus: 'in_stock' | 'low_stock' | 'out_of_stock' | 'discontinued' = 
        newStock <= selectedProduct.reorderLevel 
          ? (newStock === 0 ? 'out_of_stock' : 'low_stock')
          : 'in_stock';
          
      const updatedProduct = {
        ...selectedProduct,
        currentStock: newStock,
        lastUpdated: new Date().toISOString(),
        status: newStatus
      };

      // Update product in persistent storage
      const updatedProducts = PersistentStorage.updateArray('products', updatedProduct, []);

      // Create stock movement record
      const newMovement: StockMovement = {
        id: Date.now().toString(),
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        type: 'adjustment',
        quantity: values.quantity,
        reason: values.reason,
        timestamp: new Date().toISOString(),
        user: 'Current User',
        warehouse: selectedProduct.warehouse || 'main-warehouse',
      };

      // Save stock movement to persistent storage
      const updatedStockMovements = PersistentStorage.addToArray('stockMovements', newMovement, []);

      setInventoryData(prev => ({
        ...prev,
        products: updatedProducts,
        stockMovements: updatedStockMovements,
      }));

      message.success('Stock level is updated, and adjustment is recorded in Stock Movements history.');
      setStockAdjustmentVisible(false);
      adjustmentForm.resetFields();
      setSelectedProduct(null);
    } catch (error) {
      message.error('Failed to adjust stock');
    }
  };

  const handleGenerateReorderSuggestion = (product: Product) => {
    try {
      const suggestion = {
        ...product,
        suggestedQuantity: product.maxStock - product.currentStock,
        reorderDate: new Date().toISOString(),
      };

      notification.info({
        message: 'Reorder Suggestion Generated',
        description: `Suggested reorder quantity for ${product.name}: ${suggestion.suggestedQuantity} units`,
        placement: 'topRight',
        duration: 6,
        icon: <ShoppingCartOutlined style={{ color: '#1890ff' }} />,
      });

      message.success('System generates a reorder suggestion for the product.');
    } catch (error) {
      message.error('Failed to generate reorder suggestion');
    }
  };

  const handleViewStockMovements = (product: Product) => {
    try {
      const productMovements = inventoryData.stockMovements.filter(
        movement => movement.productId === product.id
      );

      Modal.info({
        title: `Stock Movement History - ${product.name}`,
        content: (
          <div>
            {productMovements.length > 0 ? (
              <List
                dataSource={productMovements}
                renderItem={movement => (
                  <List.Item>
                    <div>
                      <Text strong>
                        {movement.type.toUpperCase()}: {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </Text>
                      <br />
                      <Text type="secondary">{movement.reason}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {new Date(movement.timestamp).toLocaleString()} by {movement.user}
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">No stock movements found for this product.</Text>
            )}
          </div>
        ),
        width: 600,
      });

      message.success('System displays complete history of stock movements (In, Out, Adjustment, Transfer).');
    } catch (error) {
      message.error('Product not found and shows no history.');
    }
  };

  const handleScanBarcode = async (barcode: string) => {
    try {
      const product = inventoryData.products.find(p => p.barcode === barcode);
      
      if (product) {
        notification.success({
          message: 'Barcode Scan Successful',
          description: `Product found: ${product.name}`,
          placement: 'topRight',
          duration: 3,
        });
        
        // Navigate to product details or update stock
        setSelectedProduct(product);
        return true;
      } else {
        message.error('Barcode not found and prevents stock update.');
        return false;
      }
    } catch (error) {
      message.error('Barcode scanning failed');
      return false;
    }
  };

  const checkUserPermissions = (action: string): boolean => {
    // Mock permission check
    const userPermissions = ['inventory.read', 'inventory.create', 'inventory.update'];
    
    switch (action) {
      case 'create':
        return userPermissions.includes('inventory.create');
      case 'update':
        return userPermissions.includes('inventory.update');
      case 'delete':
        return userPermissions.includes('inventory.delete');
      default:
        return userPermissions.includes('inventory.read');
    }
  };

  const handlePermissionCheck = (action: string, callback: () => void) => {
    if (checkUserPermissions(action)) {
      callback();
    } else {
      setPermissionError('Access denied and prevents access.');
      message.error('Access denied and prevents access.');
    }
  };

  const productColumns: ColumnsType<Product> = [
    {
      title: 'Product',
      key: 'product',
      render: (record: Product) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.name}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            SKU: {record.sku} | {record.barcode ? `Barcode: ${record.barcode}` : 'No barcode'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag>{category}</Tag>,
    },
    {
      title: 'Stock Level',
      key: 'stock',
      render: (record: Product) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.currentStock} / {record.maxStock}
          </div>
          <Progress
            percent={(record.currentStock / record.maxStock) * 100}
            size="small"
            status={record.status === 'low_stock' ? 'exception' : 'normal'}
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          in_stock: { color: 'green', text: 'In Stock' },
          low_stock: { color: 'orange', text: 'Low Stock' },
          out_of_stock: { color: 'red', text: 'Out of Stock' },
          discontinued: { color: 'gray', text: 'Discontinued' },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => <Text code>{location}</Text>,
    },
    {
      title: 'Value',
      key: 'value',
      render: (record: Product) => (
        <div>
          <div>${(record.currentStock * record.unitPrice).toFixed(2)}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            @ ${record.unitPrice}
          </Text>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Product) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => setSelectedProduct(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Product">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handlePermissionCheck('update', () => {
                setSelectedProduct(record);
                form.setFieldsValue(record);
                setProductModalVisible(true);
              })}
            />
          </Tooltip>
          <Tooltip title="Adjust Stock">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handlePermissionCheck('update', () => {
                setSelectedProduct(record);
                setStockAdjustmentVisible(true);
              })}
            />
          </Tooltip>
          <Tooltip title="Generate Reorder">
            <Button
              type="link"
              icon={<ShoppingCartOutlined />}
              onClick={() => handleGenerateReorderSuggestion(record)}
              disabled={record.currentStock > record.reorderLevel}
            />
          </Tooltip>
          <Tooltip title="View History">
            <Button
              type="link"
              icon={<BarChartOutlined />}
              onClick={() => handleViewStockMovements(record)}
            />
          </Tooltip>
          <Tooltip title="Print Barcode">
            <Button
              type="link"
              icon={<PrinterOutlined />}
              onClick={() => message.success('Barcode print job queued')}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 使產品表支持拖拽列排序（排除 Actions 列）
  const { columns: draggableProductColumns } = useDraggableColumns<Product>({
    columns: productColumns as any,
    disableDragKeys: ['actions'],
    storageKey: 'inventory-overview-products',
  });

  const renderProductModal = () => (
    <Modal
      title={selectedProduct ? 'Edit Product' : 'Add New Product'}
      open={productModalVisible}
      onOk={() => form.submit()}
      onCancel={() => {
        setProductModalVisible(false);
        setSelectedProduct(null);
        form.resetFields();
      }}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={selectedProduct ? handleAddProduct : handleAddProduct}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="nameEnglish"
              label="Product Name (English)"
              rules={[{ required: true, message: 'Please enter English product name' }]}
            >
              <Input placeholder="Enter product name in English" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="nameChinese"
              label="Product Name (Chinese)"
              rules={[{ required: true, message: 'Please enter Chinese product name' }]}
            >
              <Input placeholder="請輸入中文產品名稱" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="sku"
              label="SKU"
              rules={[{ required: true, message: 'Please enter SKU' }]}
            >
              <Input placeholder="Enter SKU" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="merchantReference"
              label="Merchant Reference"
              tooltip="External merchant reference code for tracking"
            >
              <Input placeholder="Enter merchant reference" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="internalReference"
              label="Internal Reference"
              tooltip="Internal reference code for tracking"
            >
              <Input placeholder="Enter internal reference" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="barcode"
              label="Barcode"
            >
              <Input placeholder="Enter barcode" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please select a category' }]}
              tooltip="Select from category master data"
            >
              <Select 
                placeholder="Select category"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={getCategoryOptions()}
                notFoundContent="No categories available"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="description"
              label="Description"
            >
              <Input placeholder="Enter product description" />
            </Form.Item>
          </Col>
        </Row>

        {/* UOM Conversion Factor Section */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="baseUOM"
              label="Base UOM"
              rules={[{ required: true, message: 'Please select base unit of measure' }]}
            >
              <Select placeholder="Select base UOM">
                <Option value="pcs">Pieces (pcs)</Option>
                <Option value="kg">Kilograms (kg)</Option>
                <Option value="grams">Grams (g)</Option>
                <Option value="liters">Liters (L)</Option>
                <Option value="meters">Meters (m)</Option>
                <Option value="units">Units</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="packagingUOM"
              label="Packaging UOM"
              rules={[{ required: true, message: 'Please select packaging unit' }]}
            >
              <Select placeholder="Select packaging UOM">
                <Option value="box">Box</Option>
                <Option value="carton">Carton</Option>
                <Option value="case">Case</Option>
                <Option value="pack">Pack</Option>
                <Option value="bag">Bag</Option>
                <Option value="pallet">Pallet</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="conversionFactor"
              label="Conversion Factor"
              rules={[
                { required: true, message: 'Please enter conversion factor' },
                { type: 'number', min: 1, message: 'Conversion factor must be at least 1' }
              ]}
              tooltip="How many base units are in one packaging unit (e.g., 1 box = 24 pcs)"
            >
              <InputNumber
                min={1}
                step={1}
                style={{ width: '100%' }}
                placeholder="e.g., 24"
                addonBefore="1 packaging ="
                addonAfter="base units"
              />
            </Form.Item>
          </Col>
        </Row>



        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="unitPrice"
              label="Unit Price"
              rules={[{ required: true, message: 'Please enter unit price' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="0.00"
                prefix="$"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="costPrice"
              label="Cost Price"
              rules={[{ required: true, message: 'Please enter cost price' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="0.00"
                prefix="$"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="capacity"
              label="Warehouse Capacity"
              rules={[{ required: true, message: 'Please enter capacity' }]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="Enter capacity"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Product Dimensions & CBM Section */}
        <Divider orientation="left">Product Dimensions & CBM</Divider>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="length"
              label="Length (cm)"
              tooltip="Product length in centimeters for CBM calculation"
            >
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: '100%' }}
                placeholder="0.0"
                onChange={() => {
                  // Auto-calculate CBM when dimensions change
                  setTimeout(() => {
                    const length = form.getFieldValue('length');
                    const width = form.getFieldValue('width');
                    const height = form.getFieldValue('height');
                    if (length && width && height) {
                      const cbm = (length / 100) * (width / 100) * (height / 100);
                      form.setFieldsValue({ volume: cbm.toFixed(6) });
                    }
                  }, 100);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="width"
              label="Width (cm)"
              tooltip="Product width in centimeters for CBM calculation"
            >
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: '100%' }}
                placeholder="0.0"
                onChange={() => {
                  // Auto-calculate CBM when dimensions change
                  setTimeout(() => {
                    const length = form.getFieldValue('length');
                    const width = form.getFieldValue('width');
                    const height = form.getFieldValue('height');
                    if (length && width && height) {
                      const cbm = (length / 100) * (width / 100) * (height / 100);
                      form.setFieldsValue({ volume: cbm.toFixed(6) });
                    }
                  }, 100);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="height"
              label="Height (cm)"
              tooltip="Product height in centimeters for CBM calculation"
            >
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: '100%' }}
                placeholder="0.0"
                onChange={() => {
                  // Auto-calculate CBM when dimensions change
                  setTimeout(() => {
                    const length = form.getFieldValue('length');
                    const width = form.getFieldValue('width');
                    const height = form.getFieldValue('height');
                    if (length && width && height) {
                      const cbm = (length / 100) * (width / 100) * (height / 100);
                      form.setFieldsValue({ volume: cbm.toFixed(6) });
                    }
                  }, 100);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="weight"
              label="Weight (kg)"
              tooltip="Product weight in kilograms"
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="volume"
              label="Volume (CBM)"
              tooltip="Calculated cubic meters (auto-calculated from dimensions)"
            >
              <InputNumber
                min={0}
                step={0.000001}
                style={{ width: '100%' }}
                placeholder="Auto-calculated"
                readOnly
                addonAfter="m³"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="declaredVolume"
              label="Declared Volume (CBM)"
              tooltip="Supplier declared volume if different from calculated"
            >
              <InputNumber
                min={0}
                step={0.000001}
                style={{ width: '100%' }}
                placeholder="Optional declared CBM"
                addonAfter="m³"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="reorderLevel"
              label="Reorder Level"
              rules={[{ required: true, message: 'Please enter reorder level' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter reorder level"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="maxStock"
              label="Max Stock"
              rules={[{ required: true, message: 'Please enter max stock' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter max stock"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="initialStock"
              label="Initial Stock"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter initial stock"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="supplier"
              label="Supplier"
              rules={[{ required: true, message: 'Please select a supplier' }]}
              tooltip="Select from registered supplier list"
            >
              <Select 
                placeholder="Select supplier"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={getSupplierOptions()}
                notFoundContent="No suppliers available"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="location"
              label="Location"
              rules={[{ required: true, message: 'Please select a location' }]}
              tooltip="Select from available warehouse locations or general zones"
            >
              <Select 
                placeholder="Select warehouse location"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={getLocationOptions()}
                notFoundContent="No locations available"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );

  const renderStockAdjustmentModal = () => (
    <Modal
      title={`Stock Adjustment - ${selectedProduct?.name}`}
      open={stockAdjustmentVisible}
      onOk={() => adjustmentForm.submit()}
      onCancel={() => {
        setStockAdjustmentVisible(false);
        setSelectedProduct(null);
        adjustmentForm.resetFields();
      }}
      width={600}
    >
      <Form
        form={adjustmentForm}
        layout="vertical"
        onFinish={handleStockAdjustment}
      >
        {selectedProduct && (
          <Descriptions
            bordered
            size="small"
            style={{ marginBottom: 16 }}
            column={2}
          >
            <Descriptions.Item label="Current Stock">
              {selectedProduct.currentStock}
            </Descriptions.Item>
            <Descriptions.Item label="Reorder Level">
              {selectedProduct.reorderLevel}
            </Descriptions.Item>
            <Descriptions.Item label="Max Stock">
              {selectedProduct.maxStock}
            </Descriptions.Item>
            <Descriptions.Item label="Location">
              {selectedProduct.location}
            </Descriptions.Item>
          </Descriptions>
        )}

        <Alert
          message="Stock Adjustment"
          description="Enter positive number to increase stock, negative to decrease stock."
          type="info"
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="quantity"
              label="Adjustment Quantity"
              rules={[
                { required: true, message: 'Please enter adjustment quantity' },
                {
                  validator: (_, value) => {
                    if (!selectedProduct) return Promise.resolve();
                    if (value < 0 && Math.abs(value) > selectedProduct.currentStock) {
                      return Promise.reject('Cannot reduce stock below zero');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter adjustment quantity"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="reasonCode"
              label="Reason Code"
              rules={[{ required: true, message: 'Please select reason code' }]}
            >
              <Select placeholder="Select reason code">
                {reasonCodes.map(code => (
                  <Select.Option key={code} value={code}>
                    {code.replace('_', ' ')}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ required: true, message: 'Please enter reason' }]}
        >
          <Input placeholder="Enter adjustment reason" />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes"
        >
          <Input.TextArea
            rows={3}
            placeholder="Additional notes (optional)"
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  // Generate category options from master category settings
  const getCategoryOptions = () => {
    const categoryOptions: { value: string; label: string; disabled?: boolean }[] = [];
    
    // Mock category master data (in real app this would come from category master settings)
    const mockCategories = [
      { id: '1', code: 'CAT001', name: 'Electronics', parentId: null, status: 'active' },
      { id: '2', code: 'CAT002', name: 'Computer Hardware', parentId: '1', status: 'active' },
      { id: '3', code: 'CAT003', name: 'Office Supplies', parentId: null, status: 'active' },
      { id: '4', code: 'CAT004', name: 'Furniture', parentId: null, status: 'active' },
      { id: '5', code: 'CAT005', name: 'Raw Materials', parentId: null, status: 'active' },
      { id: '6', code: 'CAT006', name: 'Consumables', parentId: null, status: 'active' },
      { id: '7', code: 'CAT007', name: 'Stationery', parentId: '3', status: 'active' },
      { id: '8', code: 'CAT008', name: 'Packaging Materials', parentId: null, status: 'active' },
      { id: '9', code: 'CAT009', name: 'Tools & Equipment', parentId: null, status: 'active' },
      { id: '10', code: 'CAT010', name: 'Discontinued Items', parentId: null, status: 'inactive' },
    ];

    mockCategories.forEach(category => {
      const parentLabel = category.parentId 
        ? mockCategories.find(c => c.id === category.parentId)?.name 
        : null;
      
      categoryOptions.push({
        value: category.name,
        label: parentLabel 
          ? `${category.code} - ${parentLabel} > ${category.name}`
          : `${category.code} - ${category.name}`,
        disabled: category.status !== 'active'
      });
    });

    return categoryOptions;
  };

  // Generate supplier options from vendor data
  const getSupplierOptions = () => {
    const supplierOptions: { value: string; label: string; disabled?: boolean }[] = [];
    
    // Mock vendor data for suppliers (in real app this would come from vendor management)
    const mockSuppliers = [
      { id: '1', code: 'V001', name: 'ABC Supplies Inc.', status: 'active', type: 'supplier' },
      { id: '2', code: 'V002', name: 'Tech Solutions Ltd.', status: 'active', type: 'supplier' },
      { id: '3', code: 'V003', name: 'Global Manufacturing Corp', status: 'pending_approval', type: 'supplier' },
      { id: '4', code: 'V004', name: 'Office Depot', status: 'active', type: 'supplier' },
      { id: '5', code: 'V005', name: 'Industrial Components LLC', status: 'active', type: 'supplier' },
      { id: '6', code: 'V006', name: 'Quality Materials Co.', status: 'inactive', type: 'supplier' },
    ];

    mockSuppliers.forEach(supplier => {
      supplierOptions.push({
        value: supplier.name,
        label: `${supplier.code} - ${supplier.name}`,
        disabled: supplier.status !== 'active'
      });
    });

    return supplierOptions;
  };

  // Generate location options from warehouse data
  const getLocationOptions = () => {
    const locationOptions: { value: string; label: string; disabled?: boolean }[] = [];
    
    warehouses.forEach(warehouse => {
      if (warehouse.zones && warehouse.zones.length > 0) {
        warehouse.zones.forEach(zone => {
          if (zone.locations && zone.locations.length > 0) {
            zone.locations.forEach(location => {
              locationOptions.push({
                value: location.code,
                label: `${warehouse.name} > ${zone.name} > ${location.code}`,
                disabled: !location.isActive
              });
            });
          } else {
            // Add zone-level option if no specific locations
            locationOptions.push({
              value: `${warehouse.code}-${zone.code}`,
              label: `${warehouse.name} > ${zone.name} (General)`,
              disabled: !zone.isActive
            });
          }
        });
      } else {
        // Add warehouse-level option if no zones
        locationOptions.push({
          value: warehouse.code,
          label: `${warehouse.name} (General)`,
          disabled: !warehouse.isActive
        });
      }
    });

    // Add fallback options if no warehouses are configured
    if (locationOptions.length === 0) {
      locationOptions.push(
        { value: 'A-01-01-01', label: 'Main Warehouse > Zone A > A-01-01-01' },
        { value: 'A-01-01-02', label: 'Main Warehouse > Zone A > A-01-01-02' },
        { value: 'B-01-01-01', label: 'Main Warehouse > Zone B > B-01-01-01' },
        { value: 'B-01-01-02', label: 'Main Warehouse > Zone B > B-01-01-02' },
        { value: 'C-01-01-01', label: 'Distribution Center > Zone C > C-01-01-01' }
      );
    }

    return locationOptions;
  };

  const renderLowStockAlert = () => (
    <Alert
      message="Low Stock Alert"
      description={
        <div>
          <Text>The following items are below reorder level:</Text>
          <List
            size="small"
            dataSource={lowStockAlerts}
            renderItem={product => (
              <List.Item>
                <Text strong>{product.name}</Text> - Current: {product.currentStock}, Reorder: {product.reorderLevel}
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleGenerateReorderSuggestion(product)}
                >
                  Generate Reorder
                </Button>
              </List.Item>
            )}
          />
        </div>
      }
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
    />
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <BoxPlotOutlined style={{ marginRight: 8 }} />
          Warehouse Management System (WMS)
        </Title>
        <Text type="secondary">
          Comprehensive inventory tracking, stock management, and warehouse operations
        </Text>
      </div>

      {/* Permission Error Alert */}
      {permissionError && (
        <Alert
          message="Access Denied"
          description={permissionError}
          type="error"
          showIcon
          closable
          onClose={() => setPermissionError('')}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Low Stock Alerts */}
      {lowStockAlerts.length > 0 && renderLowStockAlert()}

      {/* Quick Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Products"
              value={inventoryData.totalProducts}
              prefix={<BoxPlotOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={inventoryData.totalValue}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Low Stock Items"
              value={inventoryData.lowStockItems}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Out of Stock"
              value={inventoryData.outOfStockItems}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        size={isMobile ? "small" : "large"}
        tabBarExtraContent={
          <Space direction={getSpaceDirection(isMobile)}>
            <Button 
              type="primary" 
              icon={<ScanOutlined />} 
              onClick={() => setScannerVisible(true)}
              {...getMobileButtonProps(isMobile)}
            >
              {!isMobile && "Scan Barcode"}
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadInventoryData}
              {...getMobileButtonProps(isMobile)}
            >
              {!isMobile && "Refresh"}
            </Button>
          </Space>
        }
      >
        <TabPane 
          tab={
            <span>
              <BarChartOutlined />
              {!isMobile && "Overview"}
            </span>
          } 
          key="overview"
        >
          <InventoryDashboard 
            inventoryData={inventoryData} 
            loading={loading} 
            onRefresh={loadInventoryData}
            isMobile={isMobile}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <BoxPlotOutlined />
              {!isMobile && "Products"}
            </span>
          } 
          key="products"
        >
          <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handlePermissionCheck('create', () => setProductModalVisible(true))}
                    >
                      Add Product
                    </Button>
                    <Button
                      icon={<UploadOutlined />}
                      onClick={() => setShowImportDialog(true)}
                    >
                      批量上傳
                    </Button>
                    <Button
                      icon={<ScanOutlined />}
                      onClick={() => setScannerVisible(true)}
                    >
                      Barcode Scan
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={loadInventoryData}
                      loading={loading}
                    >
                      Refresh
                    </Button>
                  </Space>
                  <Space>
                    <Search
                      placeholder="Search products..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                    <Select
                      placeholder="Filter by warehouse"
                      style={{ width: 200 }}
                      value={selectedWarehouse}
                      onChange={setSelectedWarehouse}
                      allowClear
                    >
                      {warehouses.map(wh => (
                        <Select.Option key={wh.id} value={wh.id}>
                          {wh.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Space>
                </div>

                <Table
                  columns={draggableProductColumns as any}
                  dataSource={filteredProducts.length > 0 ? filteredProducts : inventoryData.products}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    total: filteredProducts.length > 0 ? filteredProducts.length : inventoryData.products.length,
                    //pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: [10, 20, 50, 100, 500],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`,
                  }}
                />
              </Card>
        </TabPane>

        <TabPane 
          tab={
            <span>
              <StockOutlined />
              {!isMobile && "Stock"}
            </span>
          } 
          key="stock"
        >
          {/* Stock tab content */}
        </TabPane>

        <TabPane 
          tab={
            <span>
              <HomeOutlined />
              {!isMobile && "Warehouses"}
            </span>
          } 
          key="warehouses"
        >
          <WarehouseManagement 
            warehouses={warehouses}
            onWarehouseCreate={(warehouse) => {
              setWarehouses(prev => [...prev, warehouse as Warehouse]);
            }}
            onWarehouseUpdate={(id, warehouse) => {
              setWarehouses(prev => prev.map(wh => wh.id === id ? {...wh, ...warehouse} : wh));
            }}
            onWarehouseDelete={(id) => {
              setWarehouses(prev => prev.filter(wh => wh.id !== id));
            }}
            onZoneCreate={(warehouseId, zone) => {
              setWarehouses(prev => prev.map(wh => {
                if (wh.id === warehouseId) {
                  return {
                    ...wh,
                    zones: [...(wh.zones || []), zone as WarehouseZone]
                  };
                }
                return wh;
              }));
              message.success(`Zone ${zone.name || 'New'} created successfully`);
            }}
            onZoneUpdate={(warehouseId, updatedZone) => {
              setWarehouses(prev => prev.map(wh => {
                if (wh.id === warehouseId) {
                  return {
                    ...wh,
                    zones: (wh.zones || []).map(zone => 
                      zone.id === updatedZone.id ? updatedZone : zone
                    )
                  };
                }
                return wh;
              }));
              message.success(`Zone ${updatedZone.name} updated successfully`);
            }}
            onZoneDelete={(warehouseId, zoneId) => {
              setWarehouses(prev => prev.map(wh => {
                if (wh.id === warehouseId) {
                  return {
                    ...wh,
                    zones: (wh.zones || []).filter(zone => zone.id !== zoneId)
                  };
                }
                return wh;
              }));
            }}
            onLocationCreate={(warehouseId, location) => {
              message.success(`Location ${location.code || 'New'} created successfully`);
            }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <EnvironmentOutlined />
              {!isMobile && "Locations"}
            </span>
          } 
          key="locations"
        >
          <WarehouseLocationManagement />
        </TabPane>
      </Tabs>

      {/* Modals */}
      {renderProductModal()}
      {renderStockAdjustmentModal()}

      {/* Barcode Scanner */}
      <BarcodeScannerComponent
        visible={scannerVisible}
        onClose={() => setScannerVisible(false)}
        scanners={scanners}
        sessionType={scanSessionType}
        warehouseId={selectedWarehouse || 'main-warehouse'}
        onScanComplete={(scans) => {
          scans.forEach(scan => {
            if (scan.barcode) {
              handleScanBarcode(scan.barcode);
            }
          });
        }}
      />

      {/* Mass Import Dialog */}
      <MassImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        entity="products"
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default InventoryOverview; 